import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, Upload, Crown, Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { UploadForm } from "./admin/UploadForm";
import { BreadcrumbJsonLd } from "../components/JsonLd";

const TOKEN_KEY = "fv_upload_token";

export default function Creator() {
  const [input, setInput] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [token, setToken] = useState(null);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [channels, setChannels] = useState([]);

  const validate = useCallback(async (t) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/upload_tokens/${encodeURIComponent(t)}`);
      setInfo(data);
      setToken(t);
      sessionStorage.setItem(TOKEN_KEY, t);
      return true;
    } catch (err) {
      toast.error(apiError(err, "Jeton invalide"));
      sessionStorage.removeItem(TOKEN_KEY);
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    api.get("/channels").then(r => setChannels(r.data)).catch(() => {});
    const saved = sessionStorage.getItem(TOKEN_KEY);
    if (!saved) return;
    api.get(`/upload_tokens/${encodeURIComponent(saved)}`)
      .then(r => { setInfo(r.data); setToken(saved); })
      .catch(() => sessionStorage.removeItem(TOKEN_KEY));
  }, []);

  const submit = (e) => { e.preventDefault(); if (input.trim()) validate(input.trim()); };
  const logout = () => { sessionStorage.removeItem(TOKEN_KEY); setToken(null); setInfo(null); setInput(""); };
  const refresh = () => validate(token);

  const pct = info ? Math.min(100, Math.round((info.quota_used / Math.max(1, info.quota_total)) * 100)) : 0;
  const expired = info?.expires_at && new Date(info.expires_at) < new Date();

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-12" data-testid="creator-page">
      <BreadcrumbJsonLd items={[{ name: "Accueil", path: "/" }, { name: "Espace créateur", path: "/creator" }]} />
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--accent)] mb-3 flex items-center gap-2"><Crown className="w-3.5 h-3.5" /> Pack Créateur · Premium</div>
        <h1 className="font-display text-5xl sm:text-6xl">Espace créateur</h1>
        <p className="text-[color:var(--text-muted)] mt-3 max-w-2xl">Téléversez vos vidéos sur la chaîne Fedevo 2.0 TV avec le jeton reçu après votre achat. Pas de mot de passe admin nécessaire.</p>
      </div>

      {!token ? (
        <form onSubmit={submit} className="glass rounded-2xl p-8 max-w-lg space-y-4" data-testid="creator-token-form">
          <label className="block text-xs uppercase tracking-widest text-[color:var(--text-muted)]">Votre jeton d'upload
            <div className="relative mt-1">
              <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]" />
              <input data-testid="creator-token-input" value={input} onChange={e => setInput(e.target.value)} placeholder="Collez le jeton reçu après paiement" autoFocus
                className="w-full bg-[color:var(--bg-elev)] border border-[color:var(--border)] rounded-lg pl-10 pr-3 py-2.5 font-mono text-sm focus:outline-none focus:border-[color:var(--accent)] transition-colors" />
            </div>
          </label>
          <button data-testid="creator-token-submit" disabled={busy || !input.trim()} className="inline-flex items-center gap-2 bg-[color:var(--accent)] text-black font-semibold px-5 py-2.5 rounded-full text-xs uppercase tracking-widest disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Ouvrir mon espace
          </button>
          <p className="text-xs text-[color:var(--text-muted)]">
            Pas encore de pack ? <Link to="/membership" className="text-[color:var(--accent)] underline">Voir les offres Créateur & Premium</Link>
          </p>
        </form>
      ) : (
        <div className="grid lg:grid-cols-[360px_1fr] gap-6 items-start">
          <div className="glass rounded-2xl p-6 space-y-4 lg:sticky lg:top-20" data-testid="creator-quota-card">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--text-muted)]">Quota du mois</div>
              <button onClick={logout} data-testid="creator-logout" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] hover:text-white"><LogOut className="w-3 h-3" /> Changer de jeton</button>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="font-display text-6xl text-[color:var(--accent)]" data-testid="creator-quota-remaining">{info.quota_remaining}</span>
              <span className="text-sm text-[color:var(--text-muted)]">/ {info.quota_total} upload(s) restant(s)</span>
            </div>
            <div className="h-2 rounded-full bg-[color:var(--bg)] overflow-hidden">
              <div className="h-full bg-[color:var(--accent)] transition-all" style={{ width: `${pct}%` }} />
            </div>
            {info.email && <div className="text-xs text-[color:var(--text-muted)] font-mono truncate">{info.email}</div>}
            {info.expires_at && (
              <div className={`text-xs ${expired ? "text-red-400" : "text-[color:var(--text-muted)]"}`} data-testid="creator-expiry">
                {expired ? "Jeton expiré le" : "Valable jusqu'au"} {new Date(info.expires_at).toLocaleDateString("fr-FR", { dateStyle: "long" })}
              </div>
            )}
            {(info.quota_remaining <= 0 || expired) && (
              <Link to="/membership" className="block text-center bg-[color:var(--accent)] text-black font-semibold px-5 py-2.5 rounded-full text-xs uppercase tracking-widest" data-testid="creator-renew">Renouveler mon pack</Link>
            )}
          </div>
          <UploadForm channels={channels} uploadToken={token} onUploaded={refresh} title="Téléverser sur Fedevo 2.0 TV" />
        </div>
      )}
    </div>
  );
}
